"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { parseAsBoolean, useQueryState } from "nuqs";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import type { DemoTemplate } from "@ocr-monorepo/shared/demo-templates";
import { Button } from "@ocr-monorepo/ui/components/button";
import { DemoProvider, useDemoContext } from "./DemoProvider";

export function DemoSetup({ template }: { template: DemoTemplate }) {
  const [started, setStarted] = useQueryState(
    "start",
    parseAsBoolean.withDefault(false).withOptions({ history: "push" }),
  );
  if (started)
    return (
      <DemoProvider enabled>
        <Provisioning template={template} />
      </DemoProvider>
    );
  const blank = template.id === "blank-space";
  const features = blank
    ? [
        [
          "An empty workspace",
          "Create the tables and extraction fields you need.",
        ],
        [
          "Your own documents",
          "Upload a PDF and turn it into structured records.",
        ],
        [
          "The full workspace experience",
          "Explore your files, tables, and queries in the real product.",
        ],
      ]
    : [
        [
          "Sample documents and results",
          "Three fictional examples you can inspect immediately.",
        ],
        [
          "Connected tables",
          template.tables
            .flatMap((table) => [
              table.name,
              ...(table.children ?? []).map((child) => child.name),
            ])
            .map((name) => name.replaceAll("_", " "))
            .join(", ") + ", with source references.",
        ],
        [
          "Extraction ready to use",
          "Fields and extraction settings configured for your own PDF.",
        ],
        [
          "Starter queries to explore",
          "Run prepared queries against the sample records, or write your own.",
        ],
      ];
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="flex items-center justify-between px-6 py-7 lg:px-12">
        <Link href="/" className="text-2xl font-semibold">
          pdfparse
        </Link>
        <Link
          href={`/demo?template=${template.id}`}
          className="text-sm text-muted-foreground"
        >
          Back to demo spaces
        </Link>
      </header>
      <main className="mx-auto max-w-[1184px] px-6 pb-12 pt-10">
        <p className="text-[13px] text-primary">
          TRY PDFPARSE · NO ACCOUNT NEEDED
        </p>
        <h1 className="mt-3 text-[34px] font-semibold leading-[42px]">
          Your {template.name.toLowerCase()} workspace is almost ready.
        </h1>
        <p className="mt-3 max-w-3xl text-base leading-[25px] text-muted-foreground">
          Start with a prepared template. You’ll use the same workspace as the
          full product,{" "}
          {blank
            ? "ready for your own documents and extraction settings."
            : "with sample documents, linked tables, and extraction settings already in place."}
        </p>
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_288px]">
          <section
            className="overflow-hidden rounded-md border border-border bg-card"
            aria-label="Selected template"
          >
            <div className="flex items-center justify-between gap-4 border-b border-border px-7 py-6">
              <div>
                <p className="text-xs text-muted-foreground">
                  SELECTED TEMPLATE
                </p>
                <h2 className="mt-1.5 text-[22px] font-semibold">
                  {template.name}
                </h2>
              </div>
              <Button asChild variant="outline">
                <Link href={`/demo?template=${template.id}`}>
                  Change template
                </Link>
              </Button>
            </div>
            {features.map(([title, description], index) => (
              <div
                key={title}
                className="flex gap-5 border-b border-border px-7 py-[18px]"
              >
                <span className="pt-0.5 font-mono text-xs text-muted-foreground">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="text-base font-semibold">{title}</h3>
                  <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                    {description}
                  </p>
                </div>
              </div>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-4 px-7 py-[22px]">
              <p className="text-[13px] text-muted-foreground">
                Continue to create your temporary space.
              </p>
              <Button onClick={() => void setStarted(true)}>
                Continue <ArrowRight className="size-4" />
              </Button>
            </div>
          </section>
          <aside className="py-2">
            <h2 className="text-lg font-semibold">
              A private place to try it.
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Work with the included samples or upload your own files. Your demo
              is private to this session.
            </p>
            <ul className="my-5 space-y-3 border-y border-border py-5 text-base">
              <li>Up to 2 hours</li>
              <li>5 uploads · 5 MB per file</li>
              <li>15 pages of extraction</li>
              <li>
                {blank
                  ? "Your own tables and documents"
                  : "Sample files included"}
              </li>
              <li>Export CSV or JSON results</li>
            </ul>
            <p className="text-[13px] text-muted-foreground">
              Need a permanent workspace or more capacity?
            </p>
            <Link
              href="/getting-started/create-account?source=demo"
              className="mt-2 inline-block text-[13px] text-primary"
            >
              Create an account →
            </Link>
          </aside>
        </div>
      </main>
    </div>
  );
}

function Provisioning({ template }: { template: DemoTemplate }) {
  const { setupStep, error, projectSlug, tableSlug } = useDemoContext();
  const router = useRouter();
  useEffect(() => {
    if (setupStep === "complete" && projectSlug)
      router.replace(
        `/dashboard/projects/${projectSlug}${tableSlug ? `/${tableSlug}` : ""}?welcome=1`,
      );
  }, [setupStep, projectSlug, tableSlug, router]);
  const steps = [
    "Preparing your private space",
    "Setting up tables and extraction fields",
    "Adding sample documents and results",
    "Opening your workspace",
  ];
  const current =
    setupStep === "creating-project"
      ? 0
      : setupStep === "initializing"
        ? 1
        : setupStep === "copying-files"
          ? 2
          : setupStep === "configuring-limits" || setupStep === "complete"
            ? 3
            : 0;
  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-6">
      <div className="w-full max-w-lg">
        <Link href="/" className="text-2xl font-semibold">
          pdfparse
        </Link>
        <h1 className="mt-10 text-[30px] font-semibold">
          Preparing your {template.name.toLowerCase()} space.
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          We’re getting everything ready for you.
        </p>
        {error ? (
          <div
            role="alert"
            className="mt-8 rounded-md border border-border bg-card p-6"
          >
            <p>
              We couldn’t finish preparing your demo space. Your setup can be
              resumed safely.
            </p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Try again
            </Button>
            <Button variant="ghost" asChild className="ml-2">
              <Link href={`/demo?template=${template.id}`}>
                Back to templates
              </Link>
            </Button>
          </div>
        ) : (
          <ol
            className="mt-8 divide-y divide-border rounded-md border border-border bg-card px-6"
            aria-live="polite"
          >
            {steps.map((step, index) => (
              <li key={step} className="flex items-center gap-4 py-5 text-sm">
                {index < current ? (
                  <Check className="size-4 text-primary" />
                ) : index === current ? (
                  <Loader2 className="size-4 animate-spin text-primary motion-reduce:animate-none" />
                ) : (
                  <span className="w-4 font-mono text-xs text-muted-foreground">
                    {index + 1}
                  </span>
                )}
                <span
                  className={
                    index > current
                      ? "text-muted-foreground"
                      : "text-foreground"
                  }
                >
                  {step}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </main>
  );
}
